import { oc } from '@orpc/contract';
import { z } from 'zod';
import { onShiftOutput } from './presence.js';
/** One person on the team (Settings, Users tab). */
export const staffRow = z.object({
    email: z.string(),
    name: z.string().nullable(),
    department: z.string().nullable(),
    access: z.enum(['admin', 'staff']),
    active: z.boolean(),
    inactiveReason: z.enum(['slack_deactivated', 'admin']).nullable(),
    slackLinked: z.boolean(),
    /** A desk login exists for this email. */
    signedIn: z.boolean(),
    lastSignInAt: z.string().nullable(),
    /** Who made the last change: a person's name, "Slack" for Slack's own deactivation, null when never changed. */
    changedByName: z.string().nullable(),
    changedAt: z.string().nullable(),
});
export const department = z.object({ slug: z.string(), name: z.string() });
export const staffListOutput = z.object({ people: z.array(staffRow), total: z.number().int(), departments: z.array(department), viewerIsAdmin: z.boolean() });
export const staffUpdateInput = z.object({
    email: z.string(),
    department: z.string().nullable().optional(),
    access: z.enum(['admin', 'staff']).optional(),
    active: z.boolean().optional(),
});
/** One sync's health (Settings, Integrations tab). Times are ISO; all null when it never ran. */
export const integrationRow = z.object({
    key: z.string(),
    label: z.string(),
    env: z.string().nullable(),
    lastRunAt: z.string().nullable(),
    lastStatus: z.enum(['ok', 'failed', 'running']).nullable(),
    lastError: z.string().nullable(),
    lastOkAt: z.string().nullable(),
    /** A plain line of figures for the row (the AI summaries' calls and dollars); most rows have none. */
    note: z.string().nullable().optional(),
});
/** A teammate who can be @mentioned: active, with a Slack account. */
export const mentionablePerson = z.object({ email: z.string(), name: z.string(), slackUserId: z.string() });
/**
 * A teammate's Slack profile photo (Jeff, 2026-10-23), keyed every way the desk meets a person:
 * profile id (viewers, note authors), email (holders), and the names Slack and the team list use.
 * Only people who uploaded a photo; everyone else keeps their initials.
 */
export const avatarRow = z.object({ profileId: z.string().nullable(), email: z.string().nullable(), names: z.array(z.string()), url: z.string() });
export const staffContract = {
    onShift: oc.route({ method: 'POST', path: '/staff/on-shift', summary: 'Everyone Dialpad has a status for, with their department' }).output(onShiftOutput),
    mentionable: oc.route({ method: 'POST', path: '/staff/mentionable', summary: 'Teammates who can be @mentioned' }).output(z.object({ people: z.array(mentionablePerson) })),
    avatars: oc.route({ method: 'POST', path: '/staff/avatars', summary: 'Slack profile photos for the team' }).output(z.object({ people: z.array(avatarRow) })),
    list: oc.route({ method: 'POST', path: '/staff/list', summary: 'The team, every person, with departments' }).output(staffListOutput),
    update: oc.route({ method: 'POST', path: '/staff/update', summary: 'Change a person (admins only)' }).input(staffUpdateInput).output(staffRow),
    addDepartment: oc.route({ method: 'POST', path: '/staff/add-department', summary: 'Add a department (admins only)' }).input(z.object({ name: z.string().trim().min(1).max(40) })).output(department),
};
/** What the desk needs to link into Slack: the workspace, the Support Tickets list, the #support-tickets channel. */
export const slackLinks = z.object({ workspaceUrl: z.string(), teamId: z.string(), listId: z.string().nullable(), channelId: z.string().nullable() });
export const integrationsContract = {
    slackLinks: oc.route({ method: 'POST', path: '/integrations/slack-links', summary: 'Where Slack links point (null without the Slack app)' }).output(slackLinks.nullable()),
    health: oc.route({ method: 'POST', path: '/integrations/health', summary: 'Each sync: last run, status, error, last success' }).output(z.object({ rows: z.array(integrationRow) })),
};
//# sourceMappingURL=staff.js.map